import React, { useState, useEffect } from 'react';
import { Edit2, ExternalLink, X, BookOpen, Users, ChevronLeft, ChevronRight, Calendar, FileText, Youtube, Settings2, HelpCircle } from 'lucide-react';
import SmartRecommendations from './SmartRecommendations';
import GameForm from './GameForm';
import SetupDialog from './SetupDialog';
import GameSetupOverlay from './GameSetupOverlay';
import GameHelpChat from './GameHelpChat';
import { apiFetch } from '../utils/api';

const NO_IMAGE_URL = '/empty-game.jpg';

// Helper to check if game has a real image (not placeholder)
const hasRealImage = (game) => {
  return game?.image_url && 
         game.image_url.trim() !== '' && 
         game.image_url !== NO_IMAGE_URL;
};

const GamesLibrary = ({ user }) => {
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null); // Detailed view
  const [selectedGameIndex, setSelectedGameIndex] = useState(-1); // Track index for navigation
  const [editingGame, setEditingGame] = useState(null); // Editing mode
  const [isAdding, setIsAdding] = useState(false);
  const [gameSessions, setGameSessions] = useState([]); // Session history for selected game
  const [loadingSessions, setLoadingSessions] = useState(false);
  
  // Setup Dialog & Overlay states
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showSetupOverlay, setShowSetupOverlay] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState(null);
  const [setupPlayerCount, setSetupPlayerCount] = useState(4);
  const [setupSkillLevel, setSetupSkillLevel] = useState(2);
  const [gameSetupEnabled, setGameSetupEnabled] = useState(false);
  
  // Game Help Chat state
  const [showHelpChat, setShowHelpChat] = useState(false);
  
  // Expanded session detail state
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [loadingSessionDetails, setLoadingSessionDetails] = useState(false);

  useEffect(() => {
    fetchGames();
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/club/beta-features')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.enabled) {
          setGameSetupEnabled(!!data.enabled.game_setup);
        }
      })
      .catch((err) => console.warn('Beta features fetch failed:', err));
    return () => { cancelled = true; };
  }, [user?.club?.id]);

  // Fetch session history when a game is selected
  useEffect(() => {
    if (selectedGame) {
      setLoadingSessions(true);
      fetch(`/api/games/${selectedGame.id}/sessions`)
        .then(res => res.json())
        .then(data => {
          setGameSessions(data);
          setLoadingSessions(false);
        })
        .catch(err => {
          console.error(err);
          setLoadingSessions(false);
        });
    }
  }, [selectedGame]);

  const fetchGames = () => {
    fetch('/api/games')
      .then(res => res.json())
      .then(setGames)
      .catch(console.error);
  };

  // Navigate to previous/next game
  const navigateGame = (direction) => {
    if (games.length === 0) return;
    let newIndex;
    if (direction === 'prev') {
      newIndex = selectedGameIndex <= 0 ? games.length - 1 : selectedGameIndex - 1;
    } else {
      newIndex = selectedGameIndex >= games.length - 1 ? 0 : selectedGameIndex + 1;
    }
    setSelectedGameIndex(newIndex);
    setSelectedGame(games[newIndex]);
  };

  // Select game and track index
  const selectGame = (game) => {
    const index = games.findIndex(g => g.id === game.id);
    setSelectedGameIndex(index);
    setSelectedGame(game);
    
    // Record context signal for smart ads
    if (user?.id && game?.id) {
      fetch('/api/ads/signal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          signalType: 'game_viewed',
          signalValue: game.name,
          gameId: game.id
        })
      }).catch(err => console.log('Signal recording skipped'));
    }
  };

  // Group sessions by season (Aug 1 - Jun 30)
  const groupSessionsBySeason = (sessions) => {
    const groups = {};
    sessions.forEach(session => {
      const date = new Date(session.date);
      const year = date.getFullYear();
      const month = date.getMonth(); // 0-11
      
      // Determine season: Aug-Dec = year-year+1, Jan-Jun = year-1-year
      let seasonKey;
      if (month >= 7) { // Aug-Dec (7-11)
        seasonKey = `${year}-${year + 1}`;
      } else { // Jan-Jul (0-6)
        seasonKey = `${year - 1}-${year}`;
      }
      
      if (!groups[seasonKey]) {
        groups[seasonKey] = [];
      }
      groups[seasonKey].push(session);
    });
    return groups;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
  };

  // Handle session row click to toggle details
  const handleSessionRowClick = async (sessionId) => {
    if (expandedSessionId === sessionId) {
      // Close if already open
      setExpandedSessionId(null);
      setSessionDetails(null);
      return;
    }

    setExpandedSessionId(sessionId);
    setLoadingSessionDetails(true);
    
    try {
      const res = await fetch(`/api/sessions/${sessionId}`);
      const data = await res.json();
      setSessionDetails(data);
    } catch (err) {
      console.error('Error loading session details:', err);
    } finally {
      setLoadingSessionDetails(false);
    }
  };

  // Handle starting setup flow
  const handleOpenSetupDialog = () => {
    setShowSetupDialog(true);
  };

  // Handle setup dialog submission
  const handleStartSetup = async ({ playerCount, skillLevel, variant }) => {
    setSetupPlayerCount(playerCount);
    setSetupSkillLevel(skillLevel);
    setShowSetupDialog(false);
    setShowSetupOverlay(true);
    setSetupLoading(true);
    setSetupError(null);
    setSetupData(null);

    try {
      const response = await apiFetch('/api/oracle/game-setup', {
        method: 'POST',
        body: JSON.stringify({
          gameName: selectedGame?.name,
          playerCount,
          skillLevel,
          variant
        })
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        setSetupError(data.message || data.error || 'Setup-ohjeen luominen epäonnistui');
      } else {
        setSetupData(data);
      }
    } catch (err) {
      console.error('Setup fetch error:', err);
      setSetupError('Yhteysvirhe. Kokeile uudelleen.');
    } finally {
      setSetupLoading(false);
    }
  };

  // Handle closing setup overlay
  const handleCloseSetup = () => {
    setShowSetupOverlay(false);
    setSetupData(null);
    setSetupError(null);
  };

  // Handle opening help chat
  const handleOpenHelpChat = () => {
    setShowHelpChat(true);
  };

  // Handle closing help chat
  const handleCloseHelpChat = () => {
    setShowHelpChat(false);
  };

  const handleSave = async (gameData) => {
    const isUpdate = !!gameData.id;
    const url = isUpdate ? `/api/games/${gameData.id}` : '/api/games';
    const method = isUpdate ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gameData)
      });

      if (res.ok) {
        await res.json(); // Consume the response
        
        if (isUpdate) {
          // Update local state immediately (map camelCase to snake_case for consistency)
          setGames(games.map(g => 
            g.id === gameData.id 
              ? { 
                  ...g, 
                  name: gameData.name,
                  bgg_id: gameData.bggId,
                  image_url: gameData.imageUrl,
                  rules_url: gameData.rulesUrl,
                  manual_url: gameData.manualUrl,
                  video_url: gameData.videoUrl
                } 
              : g
          ));
          // Update selected game if open
          if (selectedGame && gameData.id === selectedGame.id) {
            setSelectedGame({
              ...selectedGame,
              name: gameData.name,
              bgg_id: gameData.bggId,
              image_url: gameData.imageUrl,
              rules_url: gameData.rulesUrl,
              manual_url: gameData.manualUrl,
              video_url: gameData.videoUrl
            });
          }
        } else {
          // Add new game to local state
          fetchGames(); // Fetch to get the new game with ID
        }
        
        setEditingGame(null);
        setIsAdding(false);
      } else {
        const error = await res.json();
        alert(`Virhe: ${error.error || 'Tallennus epäonnistui'}`);
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Virhe tallennuksessa');
    }
  };

  // --- DETAIL MODAL ---
  const GameDetails = ({ game, onClose, onEdit, onNavigate }) => {
      if(!game) return null;
      
      const groupedSessions = groupSessionsBySeason(gameSessions);
      const seasonKeys = Object.keys(groupedSessions).sort().reverse(); // Newest first
      
      return (
          <div className="modal-overlay" onClick={onClose}>
              <div className="modal-content card game-detail-modal" onClick={e => e.stopPropagation()}>
                  <button className="close-btn" onClick={onClose}><X /></button>
                  
                  {/* Game Image with Navigation Arrows */}
                  <div className="game-image-nav">
                    <button 
                      className="nav-arrow nav-prev" 
                      onClick={(e) => { e.stopPropagation(); onNavigate('prev'); }}
                      title="Edellinen peli"
                    >
                      <ChevronLeft size={32} />
                    </button>
                    
                    <div className="game-image-container">
                      {hasRealImage(game) ? (
                        <img 
                            src={game.image_url} 
                            alt={game.name} 
                            className="game-detail-image"
                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} 
                        />
                      ) : (
                        <div className="game-no-image" style={{
                          width: '100%',
                          height: '200px',
                          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#64748b',
                          fontSize: '3rem'
                        }}>
                          🧍
                        </div>
                      )}
                    </div>
                    
                    <button 
                      className="nav-arrow nav-next" 
                      onClick={(e) => { e.stopPropagation(); onNavigate('next'); }}
                      title="Seuraava peli"
                    >
                      <ChevronRight size={32} />
                    </button>
                  </div>
                  
                  <h2 style={{textAlign: 'center', marginTop: '1rem'}}>{game.name}</h2>
                  
                  {/* Links immediately below image */}
                  <div className="game-links" style={{display: 'flex', justifyContent: 'center', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap'}}>
                    {game.bgg_id && (
                        <a href={`https://boardgamegeek.com/boardgame/${game.bgg_id}`} target="_blank" rel="noreferrer" className="btn-secondary" title="BoardGameGeek">
                            <ExternalLink size={16} /> BGG
                        </a>
                    )}
                    {game.rules_url && !game.bgg_id && (
                        <a href={game.rules_url} target="_blank" rel="noreferrer" className="btn-secondary" title="BoardGameGeek-sivu">
                            <ExternalLink size={16} /> BGG
                        </a>
                    )}
                    {game.manual_url && (
                        <a href={game.manual_url} target="_blank" rel="noreferrer" className="btn-secondary btn-manual" title="Lataa säännöt (PDF)">
                            <FileText size={16} /> Säännöt
                        </a>
                    )}
                    {game.video_url && (
                        <a href={game.video_url} target="_blank" rel="noreferrer" className="btn-secondary btn-video" title="Katso opastusvideo">
                            <Youtube size={16} /> Video
                        </a>
                    )}
                    {user?.role === 'admin' && (
                        <button className="btn-primary" onClick={() => onEdit(game)}>
                            <Edit2 size={16} /> Muokkaa
                        </button>
                    )}
                  </div>

                  {/* Setup and Help Actions */}
                  <div className="game-actions" style={{display: 'flex', justifyContent: 'center', gap: '0.75rem', marginTop: '1rem'}}>
                    {gameSetupEnabled && (
                      <button 
                        className="btn-setup"
                        onClick={handleOpenSetupDialog}
                        title="Näytä pelin setup-ohje"
                      >
                        <Settings2 size={18} />
                        <span>Pelin Setup</span>
                      </button>
                    )}
                    <button 
                      className="btn-help"
                      onClick={handleOpenHelpChat}
                      title="Kysy säännöistä"
                    >
                      <HelpCircle size={18} />
                      <span>Kysy Apua</span>
                    </button>
                  </div>

                  {/* Session History by Season */}
                  <div className="game-history" style={{marginTop: '2rem'}}>
                    <h4>Pelihistoria</h4>
                    
                    {loadingSessions ? (
                      <p style={{color: '#888'}}>Ladataan...</p>
                    ) : gameSessions.length === 0 ? (
                      <p style={{color: '#888', fontStyle: 'italic'}}>Ei pelattuja sessioita</p>
                    ) : (
                      <div className="season-groups">
                        {seasonKeys.map(seasonKey => (
                          <details key={seasonKey} className="season-accordion" open={seasonKeys.indexOf(seasonKey) === 0}>
                            <summary className="season-header">
                              <Calendar size={16} /> Kausi {seasonKey} ({groupedSessions[seasonKey].length} peliä)
                            </summary>
                            <table className="session-history-table">
                              <thead>
                                <tr>
                                  <th>Päivä</th>
                                  <th>Kesto</th>
                                  <th><Users size={14} /></th>
                                  <th>Pisteet</th>
                                </tr>
                              </thead>
                              <tbody>
                                {groupedSessions[seasonKey].map(s => (
                                  <React.Fragment key={s.sessionId}>
                                    <tr 
                                      className={`session-row clickable-row ${expandedSessionId === s.sessionId ? 'expanded' : ''}`}
                                      onClick={(e) => { e.stopPropagation(); handleSessionRowClick(s.sessionId); }}
                                    >
                                      <td>{new Date(s.date).toLocaleDateString('fi-FI')}</td>
                                      <td>{formatDuration(s.durationMinutes)}</td>
                                      <td>{s.playerCount}</td>
                                      <td>{s.totalRawScore || '-'}</td>
                                    </tr>
                                    {/* Expanded session details */}
                                    {expandedSessionId === s.sessionId && (
                                      <tr className="session-details-row">
                                        <td colSpan="4">
                                          {loadingSessionDetails ? (
                                            <div style={{padding: '0.5rem', color: '#888'}}>Ladataan...</div>
                                          ) : sessionDetails ? (
                                            <div className="session-details-content">
                                              <div className="session-results-mini">
                                                <strong>Tulokset:</strong>
                                                <ul style={{margin: '0.5rem 0 0 0', padding: '0 0 0 1.25rem'}}>
                                                  {sessionDetails.results?.map((r, idx) => (
                                                    <li key={idx} style={{color: r.rank === 1 ? '#d35400' : '#555'}}>
                                                      {r.rank}. {r.playerName} {r.rawScore ? `(${r.rawScore} VP)` : ''} - {r.leaguePoints} MP
                                                    </li>
                                                  ))}
                                                </ul>
                                              </div>
                                              {sessionDetails.image_url && (
                                                <div className="session-image-mini">
                                                  <img 
                                                    src={sessionDetails.image_url} 
                                                    alt="Session" 
                                                    style={{maxWidth: '100px', maxHeight: '80px', borderRadius: '4px', marginTop: '0.5rem'}}
                                                  />
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <div style={{padding: '0.5rem', color: '#888'}}>Ei tietoja</div>
                                          )}
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                ))}
                              </tbody>
                            </table>
                          </details>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Smart Product Recommendations for this game */}
                  <SmartRecommendations 
                    placement="game_detail_sidebar" 
                    gameId={game.id}
                    userId={user?.id}
                    variant="inline"
                    maxProducts={2}
                  />
              </div>
          </div>
      );
  };

  return (
    <div className="games-library">
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
        <h2>Pelikirjasto</h2>
        {user?.role === 'admin' && (
          <button className="btn-primary" style={{width: 'auto'}} onClick={() => setIsAdding(true)}>
            + Lisää Peli
          </button>
        )}
      </div>

      {(isAdding || editingGame) && (
        <div className="modal-overlay">
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <GameForm 
                game={editingGame} 
                onSave={handleSave} 
                onCancel={() => { setEditingGame(null); setIsAdding(false); }} 
                />
            </div>
        </div>
      )}

      {selectedGame && !editingGame && (
          <GameDetails 
            game={selectedGame} 
            onClose={() => { setSelectedGame(null); setSelectedGameIndex(-1); setGameSessions([]); }} 
            onEdit={(g) => { setSelectedGame(null); setEditingGame(g); }}
            onNavigate={navigateGame}
          />
      )}

      <div className="game-grid">
        {games.map(game => (
          <div key={game.id} className="card game-card" onClick={() => selectGame(game)}>
            <div className="game-cover-wrapper">
                {hasRealImage(game) ? (
                  <img 
                    src={game.image_url} 
                    alt={game.name} 
                    className="game-cover"
                    onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="game-cover game-no-image" style={{
                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#64748b',
                    fontSize: '2.5rem'
                  }}>
                    🧍
                  </div>
                )}
            </div>
            <h4>{game.name}</h4>
          </div>
        ))}
      </div>

      {/* Setup Dialog */}
      {showSetupDialog && selectedGame && (
        <SetupDialog
          game={selectedGame}
          onStart={handleStartSetup}
          onCancel={() => setShowSetupDialog(false)}
          maxPlayers={12}
          minPlayers={2}
        />
      )}

      {/* Setup Overlay (Fullscreen) */}
      {showSetupOverlay && selectedGame && (
        <GameSetupOverlay
          game={selectedGame}
          playerCount={setupPlayerCount}
          skillLevel={setupSkillLevel}
          onClose={handleCloseSetup}
          setupData={setupData}
          isLoading={setupLoading}
          error={setupError}
        />
      )}

      {/* Game Help Chat (Sidebar) */}
      {showHelpChat && selectedGame && (
        <GameHelpChat
          game={selectedGame}
          onClose={handleCloseHelpChat}
          isOpen={showHelpChat}
        />
      )}

      {/* CSS for new buttons */}
      <style>{`
        .btn-setup,
        .btn-help {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1rem;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
        }

        .btn-setup {
          background: linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%);
          color: #fff;
        }

        .btn-setup:hover {
          background: linear-gradient(135deg, #9b59b6 0%, #a569bd 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(142, 68, 173, 0.3);
        }

        .btn-help {
          background: linear-gradient(135deg, #2980b9 0%, #3498db 100%);
          color: #fff;
        }

        .btn-help:hover {
          background: linear-gradient(135deg, #3498db 0%, #5dade2 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(41, 128, 185, 0.3);
        }

        @media (max-width: 480px) {
          .game-actions {
            flex-direction: column;
          }

          .btn-setup,
          .btn-help {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default GamesLibrary;
